import { useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit2, Check, X, Gauge, Plus, ChevronRight, FileText, ExternalLink, Trash2, Wrench, AlertTriangle } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useVehicle, useUpdateVehicle, useUpdateMileage, useDeleteVehicle,
         useSchedules, useAddSchedule, useDeleteSchedule, useLogService, useServiceItems,
         useIssues, useAddIssue, useDocuments, useAddDocument, useDeleteDocument,
         useServiceRecords, useJobs, useVehicleCheckins, useCheckin, useUpdateCheckin } from '../api/hooks'
import { useAuth } from '../context/AuthContext'
import { getScheduleStatus, STATUS_STYLES } from '../utils/schedule'

// ─── Shared helpers ───────────────────────────────────────────────────────────

function Plate({ registration, size = 'md' }) {
  const sizes = { sm: 'px-2 py-0.5 text-xs', md: 'px-3 py-1.5 text-sm', lg: 'px-4 py-2 text-base' }
  return (
    <span className={clsx('inline-block font-mono font-bold tracking-widest bg-yellow-400 text-black rounded', sizes[size])}>
      {registration}
    </span>
  )
}

const FUEL_STYLES = {
  petrol:   'bg-blue-500/15 text-blue-400',
  diesel:   'bg-slate-500/20 text-slate-400',
  electric: 'bg-green-500/15 text-green-400',
  hybrid:   'bg-teal-500/15 text-teal-400',
  other:    'bg-gray-500/15 text-gray-400',
}

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

const FUEL_OPTIONS  = ['petrol', 'diesel', 'electric', 'hybrid', 'other']
const TRANS_OPTIONS = ['manual', 'automatic', 'other']

// ─── Tabs ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview',   label: 'Overview' },
  { id: 'schedule',   label: 'Schedule' },
  { id: 'issues',     label: 'Issues' },
  { id: 'history',    label: 'History' },
  { id: 'documents',  label: 'Documents' },
  { id: 'checkins',   label: 'Check-Ins' },
]

// ─── Overview tab ─────────────────────────────────────────────────────────────

function InfoRow({ label, value }) {
  if (!value) return null
  return (
    <div>
      <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-0.5">{label}</p>
      <p className="text-sm text-[var(--text-primary)] font-medium capitalize">{value}</p>
    </div>
  )
}

function OverviewTab({ vehicle, canEdit }) {
  const updateVehicle = useUpdateVehicle()
  const updateMileage = useUpdateMileage()
  const deleteVehicle = useDeleteVehicle()
  const navigate      = useNavigate()

  const [editing, setEditing]       = useState(false)
  const [showMileage, setShowMileage] = useState(false)
  const [newMileage, setNewMileage]  = useState('')
  const [form, setForm]              = useState({
    make:         vehicle.make                           || '',
    model:        vehicle.model                          || '',
    year:         vehicle.year                           || '',
    colour:       vehicle.colour                         || '',
    fuelType:     vehicle.fuel_type                      || '',
    transmission: vehicle.transmission                   || '',
    engineSize:   vehicle.engine_size                    || '',
    vin:          vehicle.vin                            || '',
    notes:        vehicle.notes                          || '',
    motExpiry:    vehicle.mot_expiry?.slice(0, 10)       || '',
  })

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }))

  const handleSave = async () => {
    try {
      await updateVehicle.mutateAsync({
        id:           vehicle.id,
        make:         form.make         || null,
        model:        form.model        || null,
        year:         form.year         ? parseInt(form.year) : null,
        colour:       form.colour       || null,
        fuelType:     form.fuelType     || null,
        transmission: form.transmission || null,
        engineSize:   form.engineSize   || null,
        vin:          form.vin          || null,
        notes:        form.notes        || null,
        motExpiry:    form.motExpiry    || null,
      })
      toast.success('Vehicle updated')
      setEditing(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed')
    }
  }

  const handleMileage = async (e) => {
    e.preventDefault()
    try {
      await updateMileage.mutateAsync({ id: vehicle.id, mileage: parseInt(newMileage) })
      toast.success('Mileage updated')
      setShowMileage(false)
      setNewMileage('')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Mileage update failed')
    }
  }

  const handleRemove = async () => {
    if (!window.confirm('Remove this vehicle from your account? Service history will be preserved.')) return
    try {
      await deleteVehicle.mutateAsync(vehicle.id)
      toast.success('Vehicle removed')
      navigate('/vehicles')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Remove failed')
    }
  }

  return (
    <div className="space-y-6">
      {/* Mileage card */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2 text-[var(--text-secondary)]">
            <Gauge size={16} />
            <span className="text-sm font-medium">Current Mileage</span>
          </div>
          {canEdit && !showMileage && (
            <button
              onClick={() => { setShowMileage(true); setNewMileage(vehicle.current_mileage || '') }}
              className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              Update
            </button>
          )}
        </div>

        {showMileage ? (
          <form onSubmit={handleMileage} className="flex gap-2 mt-2">
            <input
              type="number" min="0" autoFocus
              value={newMileage}
              onChange={e => setNewMileage(e.target.value)}
              placeholder="Enter mileage"
              className={clsx(inputCls, 'flex-1')}
              required
            />
            <button type="submit" disabled={updateMileage.isPending}
              className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              <Check size={16} />
            </button>
            <button type="button" onClick={() => setShowMileage(false)}
              className="px-3 py-2 bg-[var(--bg-overlay)] text-[var(--text-muted)] rounded-lg hover:text-[var(--text-primary)]">
              <X size={16} />
            </button>
          </form>
        ) : (
          <p className="font-display text-2xl font-bold text-[var(--text-primary)] mt-1">
            {vehicle.current_mileage != null
              ? `${Number(vehicle.current_mileage).toLocaleString()} miles`
              : <span className="text-base font-normal text-[var(--text-muted)]">Not recorded</span>
            }
          </p>
        )}
      </div>

      {/* Vehicle details */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-[var(--text-primary)]">Vehicle Details</h3>
          {canEdit && (
            <div className="flex gap-2">
              {editing ? (
                <>
                  <button onClick={handleSave} disabled={updateVehicle.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                               text-white text-xs font-medium rounded-lg transition-colors">
                    <Check size={14} />
                    Save
                  </button>
                  <button onClick={() => setEditing(false)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                               text-[var(--text-secondary)] text-xs font-medium rounded-lg transition-colors">
                    <X size={14} />
                    Cancel
                  </button>
                </>
              ) : (
                <button onClick={() => setEditing(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                             text-[var(--text-secondary)] text-xs font-medium rounded-lg transition-colors">
                  <Edit2 size={14} />
                  Edit
                </button>
              )}
            </div>
          )}
        </div>

        {editing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: 'Make',         field: 'make',         type: 'text' },
              { label: 'Model',        field: 'model',        type: 'text' },
              { label: 'Year',         field: 'year',         type: 'number' },
              { label: 'Colour',       field: 'colour',       type: 'text' },
              { label: 'Engine Size',  field: 'engineSize',   type: 'text' },
              { label: 'VIN',          field: 'vin',          type: 'text' },
            ].map(({ label, field, type }) => (
              <div key={field}>
                <label className="block text-xs text-[var(--text-muted)] mb-1">{label}</label>
                <input type={type} value={form[field]} onChange={set(field)}
                  className={inputCls} />
              </div>
            ))}

            <div>
              <label className="block text-xs text-[var(--text-muted)] mb-1">Fuel Type</label>
              <select value={form.fuelType} onChange={set('fuelType')}
                className={clsx(inputCls, 'cursor-pointer')}>
                <option value="">—</option>
                {FUEL_OPTIONS.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs text-[var(--text-muted)] mb-1">Transmission</label>
              <select value={form.transmission} onChange={set('transmission')}
                className={clsx(inputCls, 'cursor-pointer')}>
                <option value="">—</option>
                {TRANS_OPTIONS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs text-[var(--text-muted)] mb-1">MOT Expiry</label>
              <input type="date" value={form.motExpiry} onChange={set('motExpiry')}
                className={inputCls} />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs text-[var(--text-muted)] mb-1">Notes</label>
              <textarea rows={2} value={form.notes} onChange={set('notes')}
                className={clsx(inputCls, 'resize-none')} />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <InfoRow label="Make"         value={vehicle.make} />
            <InfoRow label="Model"        value={vehicle.model} />
            <InfoRow label="Year"         value={vehicle.year} />
            <InfoRow label="Colour"       value={vehicle.colour} />
            <InfoRow label="Engine"       value={vehicle.engine_size} />
            <InfoRow label="Transmission" value={vehicle.transmission} />
            <InfoRow label="VIN"          value={vehicle.vin} />
            <InfoRow label="MOT Expiry"   value={vehicle.mot_expiry
              ? new Date(vehicle.mot_expiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
              : null} />
            <div className="col-span-2 sm:col-span-3">
              {vehicle.fuel_type && (
                <div>
                  <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-1">Fuel</p>
                  <span className={clsx('px-2 py-0.5 rounded text-xs font-medium capitalize',
                    FUEL_STYLES[vehicle.fuel_type] || FUEL_STYLES.other)}>
                    {vehicle.fuel_type}
                  </span>
                </div>
              )}
            </div>
            {vehicle.notes && (
              <div className="col-span-2 sm:col-span-3">
                <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-1">Notes</p>
                <p className="text-sm text-[var(--text-secondary)]">{vehicle.notes}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* MOT expiry warning */}
      {(() => {
        if (!vehicle.mot_expiry) return null
        const days = Math.ceil((new Date(vehicle.mot_expiry) - new Date()) / 86400000)
        if (days > 60) return null
        const urgent = days <= 30
        return (
          <div className={clsx('flex items-start gap-3 px-4 py-4 rounded-xl border',
            urgent
              ? 'bg-red-500/10 border-red-500/30 text-red-400'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-400')}>
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm">
                {days <= 0 ? 'MOT has expired' : `MOT expires in ${days} day${days === 1 ? '' : 's'}`}
              </p>
              <p className={clsx('text-xs mt-0.5', urgent ? 'text-red-400/80' : 'text-amber-400/80')}>
                {new Date(vehicle.mot_expiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                {days > 0 && ' — contact your garage to arrange a pre-MOT check.'}
              </p>
            </div>
          </div>
        )
      })()}

      {/* Danger zone */}
      {canEdit && (
        <div className="bg-[var(--bg-surface)] border border-red-500/20 rounded-xl p-5">
          <h3 className="text-sm font-medium text-[var(--text-primary)] mb-1">Remove Vehicle</h3>
          <p className="text-xs text-[var(--text-muted)] mb-4">
            This ends your ownership record. Service history is preserved and cannot be deleted.
          </p>
          <button
            onClick={handleRemove}
            disabled={deleteVehicle.isPending}
            className="px-4 py-2 bg-red-600/15 hover:bg-red-600/25 border border-red-500/30
                       text-red-400 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            Remove Vehicle
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Schedule tab ─────────────────────────────────────────────────────────────

function ScheduleRow({ schedule, vehicle, canEdit, onLog, onDelete }) {
  const { status, daysUntil, milesUntil } = getScheduleStatus(schedule, vehicle.current_mileage)
  const styles = STATUS_STYLES[status]
  const name   = schedule.item_name || schedule.custom_name || 'Service'

  let dueText = 'No due date set'
  if (schedule.next_due_date) {
    const d = new Date(schedule.next_due_date)
    dueText = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    if (daysUntil !== null) {
      if (daysUntil < 0)       dueText += ` · ${Math.abs(daysUntil)} days overdue`
      else if (daysUntil === 0) dueText += ' · Today'
      else                      dueText += ` · ${daysUntil} day${daysUntil === 1 ? '' : 's'}`
    }
  }
  if (schedule.next_due_mileage) {
    const mText = `${Number(schedule.next_due_mileage).toLocaleString()} miles`
    dueText = schedule.next_due_date ? `${dueText} / ${mText}` : mText
    if (milesUntil !== null && schedule.next_due_date == null) {
      dueText += milesUntil > 0 ? ` · ${Number(milesUntil).toLocaleString()} away` : ' · Due now'
    }
  }

  return (
    <div className="flex items-center gap-3 p-4 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl">
      <div className={clsx('w-2 h-2 rounded-full shrink-0', styles.dot)} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)]">{name}</p>
        <p className="text-xs text-[var(--text-secondary)] mt-0.5">{dueText}</p>
        {schedule.last_service_date && (
          <p className="text-xs text-[var(--text-muted)]">
            {'Last: '}
            {new Date(schedule.last_service_date).toLocaleDateString('en-GB', {
              day: 'numeric', month: 'short', year: 'numeric',
            })}
            {schedule.last_service_mileage
              ? ` at ${Number(schedule.last_service_mileage).toLocaleString()} miles`
              : ''}
          </p>
        )}
      </div>
      <span className={clsx('px-2 py-0.5 rounded text-xs font-medium shrink-0', styles.badge)}>
        {styles.label}
      </span>
      {canEdit && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onLog}
            className="px-2.5 py-1.5 text-xs font-medium bg-[var(--bg-overlay)] hover:bg-indigo-600
                       text-[var(--text-secondary)] hover:text-white rounded-lg transition-colors"
          >
            Log
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-[var(--text-muted)] hover:text-red-400 rounded-lg transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  )
}

function AddScheduleModal({ vehicle, serviceItems, onClose }) {
  const addSchedule = useAddSchedule()
  const [form, setForm] = useState({
    serviceItemId: '', customName: '', intervalMonths: '', intervalMiles: '',
    lastServiceDate: '', lastServiceMileage: '', notes: '',
  })
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const grouped = serviceItems.reduce((acc, item) => {
    const cat = item.category || 'general'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(item)
    return acc
  }, {})

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const isCustom = form.serviceItemId === 'custom'
      await addSchedule.mutateAsync({
        vehicleId:          vehicle.id,
        serviceItemId:      isCustom ? null : (form.serviceItemId || null),
        customName:         isCustom ? (form.customName || null) : null,
        intervalMonths:     form.intervalMonths     ? parseInt(form.intervalMonths)     : null,
        intervalMiles:      form.intervalMiles      ? parseInt(form.intervalMiles)      : null,
        lastServiceDate:    form.lastServiceDate    || null,
        lastServiceMileage: form.lastServiceMileage ? parseInt(form.lastServiceMileage) : null,
        notes:              form.notes              || null,
      })
      toast.success('Added to schedule')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Add to Schedule</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Service type</label>
            <select value={form.serviceItemId} onChange={set('serviceItemId')} className={inputCls} required>
              <option value="">Select a service…</option>
              {Object.entries(grouped).map(([cat, items]) => (
                <optgroup key={cat} label={cat.charAt(0).toUpperCase() + cat.slice(1)}>
                  {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                </optgroup>
              ))}
              <option value="custom">— Custom —</option>
            </select>
          </div>

          {form.serviceItemId === 'custom' && (
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Custom name</label>
              <input type="text" value={form.customName} onChange={set('customName')}
                placeholder="e.g. Windscreen washer fluid" className={inputCls} required />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Every (months)</label>
              <input type="number" min="1" value={form.intervalMonths} onChange={set('intervalMonths')}
                placeholder="12" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Every (miles)</label>
              <input type="number" min="1" value={form.intervalMiles} onChange={set('intervalMiles')}
                placeholder="10000" className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Last service date</label>
              <input type="date" value={form.lastServiceDate} onChange={set('lastServiceDate')} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Last mileage</label>
              <input type="number" min="0" value={form.lastServiceMileage} onChange={set('lastServiceMileage')}
                placeholder="45000" className={inputCls} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Notes</label>
            <textarea rows={2} value={form.notes} onChange={set('notes')}
              className={clsx(inputCls, 'resize-none')} />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={addSchedule.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {addSchedule.isPending ? 'Adding…' : 'Add to Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function LogServiceModal({ vehicle, schedule, onClose }) {
  const logService = useLogService()
  const today      = new Date().toISOString().slice(0, 10)
  const [form, setForm] = useState({
    serviceDate: today,
    mileage:     vehicle.current_mileage ?? '',
    notes:       '',
    cost:        '',
    performedBy: '',
  })
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))
  const name = schedule.item_name || schedule.custom_name || 'Service'

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await logService.mutateAsync({
        vehicleId:   vehicle.id,
        scheduleId:  schedule.id,
        serviceDate: form.serviceDate,
        mileage:     form.mileage     ? parseInt(form.mileage)     : null,
        notes:       form.notes       || null,
        cost:        form.cost        ? parseFloat(form.cost)      : null,
        performedBy: form.performedBy || null,
      })
      toast.success(`${name} logged`)
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to log')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-sm bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <div>
            <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Log Service</h2>
            <p className="text-sm text-[var(--text-muted)]">{name}</p>
          </div>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Date</label>
            <input type="date" value={form.serviceDate} onChange={set('serviceDate')}
              required className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Mileage</label>
              <input type="number" min="0" value={form.mileage} onChange={set('mileage')}
                placeholder="45000" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Cost (£)</label>
              <input type="number" min="0" step="0.01" value={form.cost} onChange={set('cost')}
                placeholder="0.00" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Performed by</label>
            <input type="text" value={form.performedBy} onChange={set('performedBy')}
              placeholder="Garage name or self-service" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Notes</label>
            <textarea rows={2} value={form.notes} onChange={set('notes')}
              className={clsx(inputCls, 'resize-none')} />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={logService.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {logService.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ScheduleTab({ vehicle, canEdit }) {
  const { data: schedules = [], isLoading } = useSchedules(vehicle.id)
  const { data: serviceItems = [] }         = useServiceItems()
  const deleteSchedule = useDeleteSchedule()

  const [showAdd,    setShowAdd]    = useState(false)
  const [loggingId,  setLoggingId]  = useState(null)

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this item from the schedule?')) return
    try {
      await deleteSchedule.mutateAsync({ vehicleId: vehicle.id, id })
      toast.success('Removed')
    } catch {
      toast.error('Remove failed')
    }
  }

  const loggingSchedule = loggingId ? schedules.find(s => s.id === loggingId) : null

  if (isLoading) {
    return (
      <div className="space-y-2 animate-pulse">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-16 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={15} />
            Add Schedule
          </button>
        </div>
      )}

      {schedules.length === 0 ? (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
          <p className="text-sm text-[var(--text-muted)]">No service schedule set up yet.</p>
          {canEdit && (
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Use Add Schedule to track oil changes, MOTs, and more.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {schedules.map(s => (
            <ScheduleRow
              key={s.id}
              schedule={s}
              vehicle={vehicle}
              canEdit={canEdit}
              onLog={() => setLoggingId(s.id)}
              onDelete={() => handleDelete(s.id)}
            />
          ))}
        </div>
      )}

      {showAdd && (
        <AddScheduleModal
          vehicle={vehicle}
          serviceItems={serviceItems}
          onClose={() => setShowAdd(false)}
        />
      )}

      {loggingSchedule && (
        <LogServiceModal
          vehicle={vehicle}
          schedule={loggingSchedule}
          onClose={() => setLoggingId(null)}
        />
      )}
    </div>
  )
}

// ─── Issues tab ───────────────────────────────────────────────────────────────

const SEVERITY_DOTS = {
  low: 'bg-blue-400', medium: 'bg-amber-400', high: 'bg-orange-400', critical: 'bg-red-400',
}

const ISSUE_STATUS_STYLES = {
  open:        'bg-blue-500/15 text-blue-400',
  quoted:      'bg-purple-500/15 text-purple-400',
  approved:    'bg-indigo-500/15 text-indigo-400',
  in_progress: 'bg-amber-500/15 text-amber-400',
  resolved:    'bg-green-500/15 text-green-400',
  deferred:    'bg-gray-500/15 text-gray-400',
}

const ISSUE_STATUS_LABELS = {
  open: 'Open', quoted: 'Quoted', approved: 'Approved',
  in_progress: 'In Progress', resolved: 'Resolved', deferred: 'Deferred',
}

const ISSUE_FILTERS = [
  { v: '',            l: 'All'         },
  { v: 'open',        l: 'Open'        },
  { v: 'in_progress', l: 'In Progress' },
  { v: 'resolved',    l: 'Resolved'    },
]

function VehicleReportIssueModal({ vehicle, onClose }) {
  const addIssue = useAddIssue()
  const [form, setForm] = useState({
    title: '', description: '', severity: 'medium',
    dateIdentified: new Date().toISOString().slice(0, 10),
  })
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await addIssue.mutateAsync({
        vehicleId:      vehicle.id,
        title:          form.title,
        description:    form.description    || null,
        severity:       form.severity       || null,
        dateIdentified: form.dateIdentified || null,
      })
      toast.success('Issue reported')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to report issue')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Report Issue</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Title</label>
            <input type="text" value={form.title} onChange={set('title')}
              placeholder="e.g. Strange noise from engine" className={inputCls} required />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Description</label>
            <textarea rows={3} value={form.description} onChange={set('description')}
              placeholder="Describe the issue in more detail…"
              className={clsx(inputCls, 'resize-none')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Severity</label>
              <select value={form.severity} onChange={set('severity')} className={inputCls}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Date identified</label>
              <input type="date" value={form.dateIdentified} onChange={set('dateIdentified')}
                className={inputCls} />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={addIssue.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {addIssue.isPending ? 'Reporting…' : 'Report Issue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function IssuesTab({ vehicle, canEdit }) {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState('')
  const [showReport,   setShowReport]   = useState(false)

  const filters = { vehicleId: vehicle.id, ...(statusFilter ? { status: statusFilter } : {}) }
  const { data: rawIssues = [], isLoading } = useIssues(filters)

  // Remove issues that are allocated to an active job — they live in the job now
  const issues = rawIssues.filter(i =>
    !i.linked_job_status || i.linked_job_status === 'cancelled'
  )

  if (isLoading) {
    return (
      <div className="space-y-2 animate-pulse">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-14 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {ISSUE_FILTERS.map(f => (
            <button key={f.v} onClick={() => setStatusFilter(f.v)}
              className={clsx('px-3 py-1 rounded-full text-xs font-medium transition-colors',
                statusFilter === f.v
                  ? 'bg-indigo-600 text-white'
                  : 'bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              )}>
              {f.l}
            </button>
          ))}
        </div>
        {canEdit && (
          <button
            onClick={() => setShowReport(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={14} />
            Report Issue
          </button>
        )}
      </div>

      {issues.length === 0 ? (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
          <p className="text-sm text-[var(--text-muted)]">
            {statusFilter ? 'No issues match this filter.' : 'No issues reported for this vehicle.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {issues.map(issue => (
            <button
              key={issue.id}
              onClick={() => navigate(`/issues/${issue.id}`)}
              className="w-full flex items-center gap-3 px-4 py-3.5 bg-[var(--bg-surface)] border border-[var(--border)]
                         rounded-xl hover:border-[var(--border-strong)] transition-all text-left group"
            >
              <div className={clsx('w-2 h-2 rounded-full shrink-0', SEVERITY_DOTS[issue.severity] || 'bg-gray-400')} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors truncate">
                  {issue.title}
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  {new Date(issue.date_identified).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'short', year: 'numeric',
                  })}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={clsx('px-2 py-0.5 rounded text-xs font-medium',
                  ISSUE_STATUS_STYLES[issue.status] || 'bg-gray-500/15 text-gray-400')}>
                  {ISSUE_STATUS_LABELS[issue.status] || issue.status}
                </span>
                <ChevronRight size={14} className="text-[var(--text-muted)] group-hover:text-indigo-400" />
              </div>
            </button>
          ))}
        </div>
      )}

      {showReport && (
        <VehicleReportIssueModal vehicle={vehicle} onClose={() => setShowReport(false)} />
      )}
    </div>
  )
}

// ─── Documents tab ────────────────────────────────────────────────────────────

const DOC_TYPE_LABELS = {
  mot_certificate:   'MOT',
  service_receipt:   'Service receipt',
  inspection_report: 'Inspection report',
  invoice:           'Invoice',
  quote:             'Quote',
  photo:             'Photo',
  other:             'Other',
}

const DOC_TYPE_OPTIONS = Object.entries(DOC_TYPE_LABELS)

function AddDocumentModal({ vehicle, onClose }) {
  const addDocument  = useAddDocument()
  const fileInputRef = useRef(null)
  const [mode, setMode] = useState('file')
  const [form, setForm] = useState({
    documentType: 'other', filename: '', url: '', notes: '',
  })
  const [file, setFile] = useState(null)
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleFileChange = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    setFile(f)
    if (!form.filename) setForm(p => ({ ...p, filename: f.name }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await addDocument.mutateAsync({
        vehicleId:    vehicle.id,
        documentType: form.documentType,
        filename:     form.filename || null,
        notes:        form.notes    || null,
        ...(mode === 'file' ? { file } : { url: form.url }),
      })
      toast.success('Document added')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Add Document</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="flex rounded-lg overflow-hidden border border-[var(--border)]">
            {[['file', 'Upload file'], ['url', 'Link URL']].map(([m, l]) => (
              <button key={m} type="button" onClick={() => setMode(m)}
                className={clsx('flex-1 py-2 text-xs font-medium transition-colors',
                  mode === m
                    ? 'bg-indigo-600 text-white'
                    : 'bg-[var(--bg-overlay)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]')}>
                {l}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Type</label>
            <select value={form.documentType} onChange={set('documentType')} className={inputCls}>
              {DOC_TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {mode === 'file' ? (
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">File</label>
              <button type="button" onClick={() => fileInputRef.current?.click()}
                className={clsx(inputCls, 'cursor-pointer text-left', !file && 'text-[var(--text-muted)]')}>
                {file ? file.name : 'Choose file…'}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*,.pdf" className="hidden" onChange={handleFileChange} />
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">URL</label>
              <input type="url" value={form.url} onChange={set('url')}
                placeholder="https://…" className={inputCls} required />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Filename / label</label>
            <input type="text" value={form.filename} onChange={set('filename')}
              placeholder="e.g. MOT Certificate 2025" className={inputCls} />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Notes</label>
            <textarea rows={2} value={form.notes} onChange={set('notes')}
              className={clsx(inputCls, 'resize-none')} />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={addDocument.isPending || (mode === 'file' && !file)}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {addDocument.isPending ? 'Uploading…' : 'Add Document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function DocumentsTab({ vehicle, canEdit }) {
  const { data: documents = [], isLoading } = useDocuments(vehicle.id)
  const deleteDocument = useDeleteDocument()
  const [showAdd, setShowAdd] = useState(false)

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this document?')) return
    try {
      await deleteDocument.mutateAsync(id)
      toast.success('Document removed')
    } catch {
      toast.error('Remove failed')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-2 animate-pulse">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-14 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={15} />
            Add Document
          </button>
        </div>
      )}

      {documents.length === 0 ? (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
          <p className="text-sm text-[var(--text-muted)]">No documents attached to this vehicle.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map(doc => (
            <div key={doc.id}
              className="flex items-center gap-3 px-4 py-3 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl">
              <FileText size={16} className="text-[var(--text-muted)] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                  {doc.filename || doc.url}
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  {DOC_TYPE_LABELS[doc.document_type] || doc.document_type}
                  {doc.uploaded_by_name ? ` · ${doc.uploaded_by_name}` : ''}
                  {' · '}
                  {new Date(doc.created_at).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'short', year: 'numeric',
                  })}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <a href={doc.url} target="_blank" rel="noopener noreferrer"
                  className="p-1.5 text-[var(--text-muted)] hover:text-indigo-400 rounded-lg transition-colors">
                  <ExternalLink size={14} />
                </a>
                {canEdit && (
                  <button onClick={() => handleDelete(doc.id)}
                    className="p-1.5 text-[var(--text-muted)] hover:text-red-400 rounded-lg transition-colors">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddDocumentModal vehicle={vehicle} onClose={() => setShowAdd(false)} />
      )}
    </div>
  )
}

// ─── History tab ──────────────────────────────────────────────────────────────

function HistoryTab({ vehicle }) {
  const navigate = useNavigate()
  const { data: records = [],   isLoading: rLoading } = useServiceRecords(vehicle.id)
  const { data: allJobs = [],   isLoading: jLoading  } = useJobs({ vehicleId: vehicle.id })

  const completedJobs = allJobs
    .filter(j => j.status === 'completed')
    .sort((a, b) => new Date(b.date_completed || b.created_at) - new Date(a.date_completed || a.created_at))

  const isLoading = rLoading || jLoading

  return (
    <div className="space-y-6">
      {/* Completed jobs */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--border)]">
          <h3 className="font-display font-bold text-[var(--text-primary)]">Completed Repairs</h3>
        </div>
        {jLoading ? (
          <div className="p-4 space-y-3 animate-pulse">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-12 bg-[var(--bg-overlay)] rounded-lg" />
            ))}
          </div>
        ) : completedJobs.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-[var(--text-secondary)]">No completed repairs yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {completedJobs.map(job => (
              <button
                key={job.id}
                onClick={() => navigate(`/repairs/${job.id}`)}
                className="w-full flex items-center gap-4 px-5 py-4 hover:bg-[var(--bg-base)]
                           transition-colors text-left group"
              >
                <div className="w-8 h-8 rounded-lg bg-green-600/10 flex items-center justify-center shrink-0">
                  <Wrench size={14} className="text-green-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400
                                 transition-colors truncate">
                    {job.title}
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">
                    {job.date_completed
                      ? `Completed ${new Date(job.date_completed).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short', year: 'numeric',
                        })}`
                      : ''}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  {job.invoice_total != null && (
                    <p className="text-sm font-semibold text-[var(--text-primary)]">
                      £{parseFloat(job.invoice_total).toFixed(2)}
                    </p>
                  )}
                  <ChevronRight size={14} className="text-[var(--text-muted)] group-hover:text-indigo-400 mt-1 ml-auto" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Service records */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--border)]">
          <h3 className="font-display font-bold text-[var(--text-primary)]">Service Records</h3>
        </div>
        {rLoading ? (
          <div className="p-4 space-y-3 animate-pulse">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-[var(--bg-overlay)] rounded-lg" />
            ))}
          </div>
        ) : records.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-[var(--text-secondary)]">No service records yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {records.map(r => {
              const name = r.item_name || r.custom_name || 'Service'
              const date = new Date(r.service_date).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric',
              })
              return (
                <div key={r.id} className="flex items-start gap-4 px-5 py-4">
                  <div className="w-8 h-8 rounded-lg bg-indigo-600/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Wrench size={14} className="text-indigo-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-[var(--text-primary)]">{name}</p>
                          {r.is_finalised && (
                            <span className="px-1.5 py-0.5 text-xs bg-indigo-500/15 text-indigo-400 rounded-full">
                              Finalised
                            </span>
                          )}
                        </div>
                        {r.performed_by && (
                          <p className="text-xs text-[var(--text-secondary)] mt-0.5">{r.performed_by}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs text-[var(--text-secondary)]">{date}</p>
                        {r.mileage && (
                          <p className="text-xs text-[var(--text-muted)] mt-0.5">{Number(r.mileage).toLocaleString()} mi</p>
                        )}
                        {r.cost && (
                          <p className="text-xs font-medium text-[var(--text-secondary)] mt-0.5">
                            £{parseFloat(r.cost).toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Check-Ins tab ────────────────────────────────────────────────────────────

const STATUS_CHIP = {
  submitted: 'bg-amber-500/15 text-amber-400',
  reviewed:  'bg-green-500/15 text-green-400',
}

function CheckinDetailModal({ checkinId, onClose }) {
  const { data, isLoading } = useCheckin(checkinId)
  const updateCheckin = useUpdateCheckin()

  const handleMarkReviewed = async () => {
    try {
      await updateCheckin.mutateAsync({ id: checkinId, status: 'reviewed' })
      toast.success('Marked as reviewed')
    } catch {
      toast.error('Failed to update')
    }
  }

  const categories = data?.responses
    ? data.responses.reduce((acc, r) => {
        if (!acc[r.category]) acc[r.category] = []
        acc[r.category].push(r)
        return acc
      }, {})
    : {}

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-lg bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl
                      flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)] shrink-0">
          <div>
            <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">
              {data?.template_name || 'Check-In'}
            </h2>
            {data?.submitted_at && (
              <p className="text-xs text-[var(--text-muted)]">
                {new Date(data.submitted_at).toLocaleDateString('en-GB', {
                  day: 'numeric', month: 'long', year: 'numeric',
                })}
                {data.mileage ? ` · ${Number(data.mileage).toLocaleString()} miles` : ''}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-5">
          {isLoading ? (
            <div className="space-y-3 animate-pulse">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-10 bg-[var(--bg-overlay)] rounded-lg" />
              ))}
            </div>
          ) : (
            <>
              {data?.flagged_count > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
                  <p className="text-sm font-medium text-amber-400">
                    {data.flagged_count} flagged item{data.flagged_count !== 1 ? 's' : ''} — vehicle issues have been created
                  </p>
                </div>
              )}

              {Object.entries(categories).map(([category, responses]) => (
                <div key={category}>
                  <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2">
                    {category}
                  </p>
                  <div className="space-y-2">
                    {responses.map(r => (
                      <div key={r.id}
                        className={clsx(
                          'flex items-center justify-between px-3 py-2.5 rounded-lg',
                          r.is_flagged
                            ? 'bg-red-500/10 border border-red-500/20'
                            : 'bg-[var(--bg-base)] border border-[var(--border)]'
                        )}>
                        <span className={clsx('text-sm', r.is_flagged ? 'text-red-400' : 'text-[var(--text-secondary)]')}>
                          {r.label}
                        </span>
                        <span className={clsx(
                          'text-sm font-semibold',
                          r.is_flagged ? 'text-red-400' : 'text-[var(--text-primary)]'
                        )}>
                          {r.value || '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {data?.notes && (
                <div>
                  <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2">Notes</p>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{data.notes}</p>
                </div>
              )}
            </>
          )}
        </div>

        {data?.status === 'submitted' && (
          <div className="px-6 py-4 border-t border-[var(--border)] shrink-0">
            <button
              onClick={handleMarkReviewed}
              disabled={updateCheckin.isPending}
              className="w-full py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors"
            >
              {updateCheckin.isPending ? 'Saving…' : 'Mark as Reviewed'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function CheckinsTab({ vehicle }) {
  const { data: checkins = [], isLoading } = useVehicleCheckins(vehicle.id)
  const [viewingId, setViewingId] = useState(null)

  if (isLoading) {
    return (
      <div className="space-y-2 animate-pulse">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-14 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl" />
        ))}
      </div>
    )
  }

  if (checkins.length === 0) {
    return (
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
        <p className="text-sm text-[var(--text-muted)]">No check-ins submitted yet.</p>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          Check-in schedules are set up from the garage side. Once a reminder is sent and submitted it will appear here.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {checkins.map(c => (
        <button
          key={c.id}
          onClick={() => setViewingId(c.id)}
          className="w-full flex items-center gap-3 px-4 py-3.5 bg-[var(--bg-surface)] border border-[var(--border)]
                     rounded-xl hover:border-[var(--border-strong)] transition-all text-left group"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors">
              {c.template_name || 'Check-In'}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-xs text-[var(--text-muted)]">
                {new Date(c.submitted_at).toLocaleDateString('en-GB', {
                  day: 'numeric', month: 'short', year: 'numeric',
                })}
              </p>
              {c.mileage && (
                <p className="text-xs text-[var(--text-muted)]">
                  · {Number(c.mileage).toLocaleString()} mi
                </p>
              )}
              {c.flagged_count > 0 && (
                <span className="text-xs text-red-400 font-medium">
                  · {c.flagged_count} flagged
                </span>
              )}
            </div>
          </div>
          <span className={clsx('px-2 py-0.5 rounded text-xs font-medium shrink-0',
            STATUS_CHIP[c.status] || 'bg-gray-500/15 text-gray-400')}>
            {c.status === 'submitted' ? 'Submitted' : 'Reviewed'}
          </span>
          <ChevronRight size={14} className="text-[var(--text-muted)] group-hover:text-indigo-400 shrink-0" />
        </button>
      ))}

      {viewingId && (
        <CheckinDetailModal checkinId={viewingId} onClose={() => setViewingId(null)} />
      )}
    </div>
  )
}

// ─── Placeholder tab ──────────────────────────────────────────────────────────

function ComingSoonTab({ label, phase }) {
  return (
    <div className="p-6 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl">
      <p className="text-[var(--text-secondary)] text-sm">{label} — coming in Phase {phase}.</p>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function VehicleDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: vehicle, isLoading, error } = useVehicle(id)
  const [activeTab, setActiveTab] = useState('overview')

  const canEdit = user?.role === 'customer' || user?.role === 'garage_admin' || user?.role === 'platform_admin'

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-[var(--bg-surface)] rounded-lg" />
        <div className="h-64 bg-[var(--bg-surface)] rounded-xl" />
      </div>
    )
  }

  if (error || !vehicle) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/vehicles')}
          className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
          <ArrowLeft size={16} /> Back to Vehicles
        </button>
        <p className="text-[var(--text-secondary)]">Vehicle not found.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-12">
      {/* Back button */}
      <button
        onClick={() => navigate('/vehicles')}
        className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
      >
        <ArrowLeft size={16} />
        Vehicles
      </button>

      {/* Header */}
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <Plate registration={vehicle.registration} size="lg" />
          </div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
            {vehicle.make} {vehicle.model}
          </h1>
          <p className="text-[var(--text-secondary)] mt-1">
            {vehicle.year ? vehicle.year : 'Year unknown'}
            {vehicle.colour ? ` · ${vehicle.colour}` : ''}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--border)] overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px',
              activeTab === tab.id
                ? 'border-indigo-500 text-indigo-400'
                : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview'  && <OverviewTab vehicle={vehicle} canEdit={canEdit} />}
      {activeTab === 'schedule'  && <ScheduleTab vehicle={vehicle} canEdit={canEdit} />}
      {activeTab === 'issues'    && <IssuesTab    vehicle={vehicle} canEdit={canEdit} />}
      {activeTab === 'history'   && <HistoryTab vehicle={vehicle} />}
      {activeTab === 'documents' && <DocumentsTab  vehicle={vehicle} canEdit={canEdit} />}
      {activeTab === 'checkins'  && <CheckinsTab vehicle={vehicle} />}
    </div>
  )
}
